import { DIALOG } from "helper/enum/dialogEnum";
import {
  clearDialog,
  openDialog,
  setupDialog,
} from "redux-store/static/redux/dialog";
import { store } from "redux-store/store";
import { ReduxStateType } from "redux-store/types";
import CryptoJS from "crypto-js";

type Pending = {
  resolve: (sectionToken: string) => void;
  reject: (reason?: unknown) => void;
};

let pending: Pending | null = null;

/**
 * Mở ModalVerify và trả Promise chỉ **resolve sau** khi API xác thực OTP thành công
 * và có section token. HttpClient `onRequireOtp` dùng hàm này.
 *
 * Modal / saga gọi `resolveIncomeSectionOtp` / `rejectIncomeSectionOtp` / `cancelIncomeSectionOtp`
 * — không resolve ngay khi mở modal.
 */
export function requestIncomeSectionOtp(signal?: AbortSignal): Promise<string> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    if (pending) {
      pending.reject(new Error("HttpClient: luồng OTP đang chờ bị thay thế"));
      pending = null;
    }
    pending = { resolve, reject };
    store.dispatch(setupDialog({ key: DIALOG.MODAL_VERIFY_INCOME }));
    store.dispatch(openDialog({ key: DIALOG.MODAL_VERIFY_INCOME }));

    const onAbort = () => {
      store.dispatch(clearDialog({ key: DIALOG.MODAL_VERIFY_INCOME }));
      if (pending) {
        pending.reject(new DOMException("Aborted", "AbortError"));
        pending = null;
      }
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/** Sau API verify/setup OTP thành công — truyền section token cho HttpClient */
export function resolveIncomeSectionOtp(sectionToken: string): void {
  if (pending) {
    pending.resolve(sectionToken);
    pending = null;
  }
}

/** API xác thực OTP lỗi — HttpClient / saga nhận reject */
export function rejectIncomeSectionOtp(error: unknown): void {
  if (pending) {
    pending.reject({
      type: ReduxStateType.UNAUTHORIZED,
      description: "User cancelled OTP verification",
    });
    pending = null;
  }
}

/** User đóng modal / hủy — không phải lỗi API */
export function cancelIncomeSectionOtp(reason?: unknown): void {
  if (pending) {
    pending.reject({
      type: ReduxStateType.UNAUTHORIZED,
      description: "User cancelled OTP verification",
    });
    pending = null;
  }
}

/**
 * Tạo AES key từ keyToken (giống BE: SHA-256)
 */
export const generateAesKey = (keyToken?: string) => {
  if (!keyToken) return null;
  return CryptoJS.SHA256(keyToken);
};

/**
 * Giải mã AES/CBC/PKCS5Padding (BE: IV + ciphertext -> base64)
 */
export const decryptAesToString = (
  encrypted: string | null | undefined,
  key: CryptoJS.lib.WordArray | null
): string | null => {
  if (!encrypted || !key) return null;

  try {
    const combined = CryptoJS.enc.Base64.parse(encrypted);

    // phải có ít nhất 16 bytes IV
    if (combined.sigBytes <= 16) return null;

    // IV = 16 bytes đầu
    const iv = CryptoJS.lib.WordArray.create(combined.words.slice(0, 4), 16);

    // ciphertext = phần còn lại
    const cipherText = CryptoJS.lib.WordArray.create(
      combined.words.slice(4),
      combined.sigBytes - 16
    );

    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: cipherText } as any,
      key,
      {
        iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      }
    );

    const text = CryptoJS.enc.Utf8.stringify(decrypted);
    return text || null;
  } catch (e) {
    return null;
  }
};

/**
 * Giải mã ra number (Long bên BE)
 */
export const decryptAesToNumber = (
  encrypted: string | null | undefined,
  key: CryptoJS.lib.WordArray | null
): number | null => {
  const text = decryptAesToString(encrypted, key);
  if (!text) return null;

  const num = Number(text);
  return Number.isNaN(num) ? null : num;
};
